// Copyright 2024, Imprivata, Inc.  All rights reserved.

import {
  Authn,
  AppError as AuthnAppError,
  ErrorCode as AuthnErrorCode,
  AuthnMethod,
  AuthnViews,
  type EventPayload,
  EventType,
  FatalError,
} from '@imprivata-cloud/authn';
import {
  createSpanName,
  debug,
  error,
  ImprHeaders,
  SpanComponent,
} from '@imprivata-cloud/common';
import {
  type FeatureFlagsProp,
  SelfServiceEventTypes,
  useEventListener,
} from '@imprivata-cloud/core';
import { Col, Layout, Row } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import {
  FACTOR_GLYPHS,
  gateGlyphOf,
  isAuthnMethod,
  SCREEN_GLYPHS,
} from '@/factors/factor-glyphs';
import { UserNameForm } from '@/factors/username-form';

import { getOrgPreferences, setDefaultHeader } from '../../api/api';
import {
  createClientUserSession,
  determineAuthMethod,
  lookupTenant,
} from '../../api/tenantLookupApi';
import DefaultLogofrom from '../../assets/default_logo.svg?react';
import UserHex from '../../assets/face-user.svg?react';
import { LoginComponent } from '../../components/login/LoginComponent';
import Separator from '../../components/separator/Separator';
import { QueryParam } from '../../constants';
import {
  CAMERA_EVENTS,
  cameraEventOutcome,
  isAuthnErrorCode,
} from '../../errorHandler/camera';
import { ErrorCode as TenantErrorCode } from '../../errorHandler/constants';
import { useNotifications } from '../../errorHandler/context/Notifications';
import { errorConfiguration } from '../../errorHandler/ErrorConfig';
import { InsufficientFactors } from '../../errorHandler/error-views/insufficient-factors';
import { InformationDialog } from '../../errorHandler/error-views/no-inline-enrollment';
import { AppError } from '../../errorHandler/errors';
import { WEB_APP_NAME } from '../../shared/constants';
import {
  AuthAction,
  AuthResource,
  type DetermineAuthActionResponse,
} from '../../shared/types';
import {
  determineResource,
  getTenantSubdomain,
  getUrlParam,
  getUrlWithTenantId,
  hasDisableAutoComplete,
} from '../../utils';
import { AuthnLayout, LayoutHeaders } from '../Card/authn-layout';
import './styles.less';

const performLookup = async (email: string, workflowId: string) => {
  const response = await lookupTenant(email, workflowId);
  // User is redirected to lookup only when tenantId is not known.
  const tenantList = response?.tenantList ?? [];
  if (tenantList.length === 0) {
    throw new AppError({ code: TenantErrorCode.TENANT_NOT_FOUND });
  }
  return tenantList.length === 1
    ? tenantList[0]
    : tenantList.find(t => t.tenantType === 'PRIMARY');
};

const updateFaceAuthQueryParams = (
  launchAuthnUi: DetermineAuthActionResponse['launchAuthnUi'],
) => {
  if (!launchAuthnUi) return;

  const url = new URL(window.location.href);
  url.searchParams.set(
    QueryParam.ShowVideo,
    String(launchAuthnUi.faceAuthVideoStreamEnabled),
  );
  url.searchParams.set(
    QueryParam.UserCanShowLiveFeed,
    String(launchAuthnUi.userCanShowLiveFeed),
  );
  window.history.replaceState(window.history.state, '', url.toString());
};

export const getTenantIdAndTargetUrl = () => {
  const targetQueryParam = getUrlParam('target');

  let tenantId = '';
  const targetUrl = targetQueryParam || '';

  if (targetQueryParam) {
    try {
      const urlObj = new URL(targetQueryParam);
      tenantId =
        urlObj.searchParams.get('tenantId') ||
        getTenantSubdomain(targetQueryParam);
    } catch {
      tenantId = getTenantSubdomain(targetQueryParam);
    }
  }

  return { tenantId, targetUrl };
};

const determineAuthAction = async (
  email: string | undefined,
  workflowId: string,
) => {
  const targetQueryParam = getUrlParam('target');

  let { tenantId, targetUrl } = getTenantIdAndTargetUrl();

  if (!tenantId && email) {
    const tenantIdFromLookup = await performLookup(email, workflowId);
    tenantId = tenantIdFromLookup?.tenantId || '';
    targetUrl = getUrlWithTenantId(targetQueryParam || '', tenantId);
  }

  if (!tenantId) {
    throw new AppError({ code: TenantErrorCode.TENANT_NOT_FOUND });
  }

  console.log('[determineAuthAction] targetUrl: ', targetUrl);

  const payload = {
    username: email,
    resource: determineResource(targetQueryParam || ''),
    targetUrl,
  };

  const authActionRes = await determineAuthMethod(
    payload,
    tenantId,
    workflowId,
  );

  if (
    !authActionRes ||
    (authActionRes?.action === AuthAction.START_IDP_SSO &&
      !authActionRes.startIdpSso?.startSsoUrl)
  ) {
    throw new AppError({ code: TenantErrorCode.TENANT_NOT_FOUND });
  }

  return { tenantId, targetUrl, ...authActionRes }; //TODO think through the return type
};

export type AuthDetails = {
  applicationId: string;
  tenantId: string;
  username?: string;
  targetUrl: string;
};

// The face-capture screen positions the shared banner over the video frame; the
// class is `.authn-layout-face-banner` (containers/Card/authn-layout.less, the
// layout that owns the slot) and rides on the emitted event so the one
// NotificationSystem renders it in place.
const FACE_BANNER_CLASS = 'authn-layout-face-banner';

const INFO_TITLE_KEYS: Record<AuthResource, string> = {
  [AuthResource.AM_PORTAL]: 'tenant-lookup.info.title',
  [AuthResource.SELF_SERVICE]: 'tenant-lookup.info.self-service-title',
};

const TenantLogin = () => {
  const { t } = useTranslation();

  const { emitError, emitCameraError, clearErrorNotifications } =
    useNotifications();

  const [actualView, setActualView] = useState<AuthnViews>();
  // Which face ceremony is on screen. Camera copy names what the user was trying
  // to do ("to enroll your face" vs "for face authentication"), so a camera
  // failure needs this; START_ENROLLING is the only signal for the enroll gate,
  // whose intro and consent screens emit no VIEW_CHANGED. Kept in a ref, not
  // state, because handleEvent is <Authn>'s `onEvent` and authn memoizes its whole
  // prop bag on it — a new identity re-creates its api service and the store
  // holding the mid-ceremony session.
  const enrollRef = useRef(false);
  // Whether the face-capture screen is on view. The shared banner is repositioned
  // over the video there, so it is tracked the same way as the ceremony above:
  // reading `actualView` inside handleEvent would be stale (it is not a dependency)
  // and adding it would churn <Authn>'s onEvent identity. Set solely by VIEW_CHANGED.
  const faceRef = useRef(false);
  // A terminal outcome is "some screen is owed", which is not the same as "a code
  // was recognized": authn escalates raw backend codes outside its own enum to
  // FatalError too, and those must still land on the generic dialog rather than
  // back on authn's opaque surface. So presence drives the screen and the
  // optional code only selects which one.
  const [terminalError, setTerminalError] = useState<{
    code?: AuthnErrorCode;
  }>();
  const [authDetails, setAuthDetails] = useState<AuthDetails>();
  const [workflowId, setWorkflowId] = useState<string>();
  const [header, setHeader] = useState<LayoutHeaders>(
    LayoutHeaders.ENROLL_CONSENT,
  );
  const [logoUrl, setLogoUrl] = useState<
    string | null | undefined | React.ReactNode
  >(undefined);
  const [customHexagonImage, setCustomHexagonImage] = useState<
    string | React.ReactNode
  >();

  const disableAutoComplete = hasDisableAutoComplete(window.location.search);
  const resource = determineResource(getUrlParam('target') || '');

  const handleSubmit = useCallback(
    async (email?: string) => {
      try {
        const workflowId = uuidv4();
        setWorkflowId(workflowId);
        const authActionRes = await determineAuthAction(email, workflowId);

        if (authActionRes?.tenantId) {
          setDefaultHeader(ImprHeaders.ImprTenantId, authActionRes.tenantId);
          setHeader(LayoutHeaders.AUTHENTICATION);

          try {
            const { logoUrl } = await getOrgPreferences();
            setLogoUrl(logoUrl || <DefaultLogofrom />);
          } catch (e) {
            console.log(e);
            setLogoUrl(<DefaultLogofrom />);
          }
        }

        if (
          authActionRes?.action === AuthAction.START_IDP_SSO &&
          authActionRes?.startIdpSso?.startSsoUrl
        ) {
          window.location.href = authActionRes?.startIdpSso?.startSsoUrl;
        } else {
          updateFaceAuthQueryParams(authActionRes?.launchAuthnUi);
          setAuthDetails({
            tenantId: authActionRes?.tenantId,
            applicationId: authActionRes?.launchAuthnUi?.applicationId || '',
            username: email,
            targetUrl: authActionRes?.targetUrl || '',
          });
        }
      } catch (err) {
        console.error(err);
        emitError(TenantErrorCode.TENANT_NOT_FOUND);
      }
    },
    [emitError],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: tenant lookup auto-submit should only run once when the URL already has a tenantId.
  useEffect(() => {
    const { tenantId } = getTenantIdAndTargetUrl();
    if (tenantId) {
      handleSubmit();
    }
  }, []);

  const tracing = useMemo(
    () => ({
      workflowId: workflowId || '',
    }),
    [workflowId],
  );

  const requestConfig = useMemo(
    () => ({
      clientName: WEB_APP_NAME,
      username: authDetails?.username,
    }),
    [authDetails],
  );

  const deviceFactors = useMemo(
    () => [
      AuthnMethod.FACE_ID,
      AuthnMethod.PASSWORD,
      AuthnMethod.IMPR_ID,
      AuthnMethod.IMPR_PIN,
      AuthnMethod.TEMP_CODE,
      AuthnMethod.EMAIL_OTP,
    ],
    [],
  );

  const restartFlow = useCallback(() => {
    setLogoUrl(undefined);
    setHeader(LayoutHeaders.ENROLL_CONSENT);
    setAuthDetails(undefined);
    setTerminalError(undefined);
    setCustomHexagonImage(<UserHex />);
  }, []);

  const onSuccess = useCallback(async () => {
    try {
      debug('Processing authentication with code');
      console.log('[onSuccess] authDetails: ', authDetails);
      if (!authDetails?.targetUrl) {
        error(
          'Target URL is not defined',
          'Cannot redirect to an undefined target URL',
        );
        return;
      }
      //call create client session API
      await createClientUserSession(authDetails?.tenantId || '', workflowId);
      debug('Redirecting to target', { targetUrl: authDetails?.targetUrl });
      window.location.href = authDetails?.targetUrl;
    } catch (err) {
      error('Authentication failed', String(err));
      emitError(err instanceof AppError ? err : TenantErrorCode.INTERNAL, {
        overrideClasses: faceRef.current ? FACE_BANNER_CLASS : undefined,
      });
      throw err;
    }
  }, [authDetails, workflowId, emitError]);

  // Host-owned terminal screens for the FatalError outcomes authn delegates.
  // Unmapped codes fall back to the simple generic InformationDialog below.
  const errorScreens = useMemo<
    Partial<Record<AuthnErrorCode, React.ReactElement>>
  >(
    () => ({
      [AuthnErrorCode.FACTORS_INSUFFICIENT]: <InsufficientFactors />,
    }),
    [],
  );
  // Handle events coming from AuthnModule
  const handleEvent = useCallback(
    ({ event, data }: EventPayload) => {
      debug('Received event', { event, data });
      createSpanName(SpanComponent.UI, 'event', `on-event--${event}`);
      const errorCode =
        data instanceof AuthnAppError
          ? data.code
          : typeof data === 'string'
            ? data
            : undefined;
      // On the face-capture screen the banner is positioned over the video frame.
      const overrideClasses = faceRef.current
        ? 'authn-layout-face-banner'
        : undefined;
      const emitAuthnError = (fallbackCode = TenantErrorCode.INTERNAL) => {
        emitError(errorCode ?? fallbackCode, { overrideClasses });
      };

      // Camera availability is decided by the shared mapping so this container
      // cannot silently drop a code again (a denied camera used to fall through
      // to no message at all).
      if (CAMERA_EVENTS.has(event)) {
        const outcome = cameraEventOutcome(event, data);
        switch (outcome.kind) {
          case 'banner':
            // Nothing else distinguishes the two ceremonies, so the absence of
            // the enroll gate is authentication.
            emitCameraError(
              outcome.code,
              enrollRef.current ? 'enroll' : 'auth',
              { overrideClasses, persistOnInteraction: true },
            );
            break;
          case 'terminal':
            clearErrorNotifications();
            setTerminalError({ code: outcome.code });
            break;
          case 'clear':
            clearErrorNotifications();
            break;
          case 'ignore':
            break;
        }
        return;
      }

      switch (event) {
        case EventType.AUTHENTICATED:
          void onSuccess();
          break;
        case EventType.CANCEL:
          // Decline-consent now holds in authn and emits CANCEL; the host
          // disposes by returning the user to the start of the flow.
          restartFlow();
          break;
        case EventType.ERROR:
          // In-flow (non-terminal) outcomes are handled by authn's own UI; the
          // host only clears any lingering banner.
          if (
            errorCode === AuthnErrorCode.LIVENESS_FAILED ||
            errorCode === AuthnErrorCode.FACTOR_LOCK_PERSISTENT ||
            errorCode === AuthnErrorCode.FACTOR_LOCK_TEMPORARY ||
            errorCode === AuthnErrorCode.FACTOR_CAPTURE_FAILED
          ) {
            clearErrorNotifications();
            break;
          }
          if (errorCode === AuthnErrorCode.FACE_TIMEOUT) {
            restartFlow();
            break;
          }

          // A network failure is transient, so it returns the user to the
          // username step rather than a terminal screen. A tenant resolved from
          // the email must be looked up again for whatever username comes next;
          // one fixed by the URL holds for any username.
          if (
            errorCode === AuthnErrorCode.NETWORK_ERROR ||
            errorCode === AuthnErrorCode.ERR_NETWORK
          ) {
            if (getTenantIdAndTargetUrl().tenantId) {
              setAuthDetails(current =>
                current ? { ...current, username: undefined } : current,
              );
            } else {
              restartFlow();
            }
            break;
          }

          if (isAuthnErrorCode(errorCode) && errorCode in errorScreens) {
            clearErrorNotifications();
            setTerminalError({ code: errorCode });
            break;
          }
          // Terminal outcomes: authn now holds an opaque surface for any
          // FatalError and delegates the terminal screen to the host. Render
          // the mapped host screen (or the generic fallback) in place of
          // <Authn> via terminalError, mirroring icp-core's errorScreens.
          if (data instanceof FatalError) {
            clearErrorNotifications();
            // A code outside the module's vocabulary still has to strand the
            // user on a screen rather than back on the opaque authn surface, so
            // it resolves to the unmapped generic dialog.
            setTerminalError({
              code: isAuthnErrorCode(errorCode) ? errorCode : undefined,
            });
            break;
          }

          if (errorCode === AuthnErrorCode.PUSH_REJECTED) {
            emitAuthnError();
            restartFlow();
            break;
          }

          if (errorCode === AuthnErrorCode.CREDENTIAL_EXPIRED) {
            emitAuthnError();
            restartFlow();
            break;
          }

          if (
            errorCode === AuthnErrorCode.ACCOUNT_LOCKED ||
            errorCode === AuthnErrorCode.USER_NOT_FOUND
          ) {
            emitAuthnError();
            restartFlow();
            const { tenantId } = getTenantIdAndTargetUrl();
            if (tenantId) {
              handleSubmit();
            }
          } else if (errorCode && errorCode in errorConfiguration) {
            emitAuthnError();
          } else {
            emitError(TenantErrorCode.INTERNAL, { overrideClasses });
          }
          break;
        case EventType.USERNAME_CHANGE_REQUESTED:
          restartFlow();
          break;
        case EventType.ONLINE:
        case EventType.CLEAR_BANNER:
          clearErrorNotifications();
          break;
        case EventType.VIEW_CHANGED:
          clearErrorNotifications();
          faceRef.current = data === AuthnViews.FACE_ID;
          // A named screen owns the glyph outright. The cleanup emit carries no
          // screen: inside a gate it hands the glyph back to the gate's flow,
          // outside one it leaves the active factor's glyph alone.
          if (data) {
            setCustomHexagonImage(SCREEN_GLYPHS[data]);
          } else if (enrollRef.current) {
            setCustomHexagonImage(gateGlyphOf(undefined));
          }
          setActualView(data);
          break;
        case EventType.TERMINATE_SESSION:
          if (data === 'timeout') {
            clearErrorNotifications();
            restartFlow();
          }
          break;
        // TIMEOUT carries no factor payload, so it clears the glyph.
        case EventType.TIMEOUT:
        case EventType.START_AUTHENTICATING:
          setCustomHexagonImage(
            isAuthnMethod(data) ? FACTOR_GLYPHS[data] : undefined,
          );
          break;
        // The gates (ME-11279) still bound the flow: their intro screens name
        // themselves, but the consent and loader steps in between do not, so the
        // mount covers those and the unmount clears the gate's glyph.
        case EventType.START_ENROLLING:
          // Also the ceremony signal a camera failure needs (see enrollRef).
          enrollRef.current = !!data;
          setCustomHexagonImage(data ? gateGlyphOf(undefined) : undefined);
          break;
        case EventType.START_RECONSENT:
          setCustomHexagonImage(data ? gateGlyphOf(undefined) : undefined);
          break;
        case EventType.INTERNAL_ERROR:
          emitAuthnError(TenantErrorCode.UNKNOWN);
          break;
        case EventType.FINISH_AUTHENTICATING:
          if (data === AuthnMethod.PASSWORD || data === AuthnMethod.TEMP_CODE) {
            setCustomHexagonImage(<UserHex />);
          }
          break;
      }
    },
    [
      emitError,
      emitCameraError,
      onSuccess,
      restartFlow,
      handleSubmit,
      clearErrorNotifications,
      errorScreens,
    ],
  );

  // Reset the username form after the user requests a temp code
  useEventListener(event => {
    if (event.type === SelfServiceEventTypes.ACCOUNT_RECOVERY_ACCEPTED) {
      setAuthDetails(current =>
        current ? { ...current, username: undefined } : current,
      );
    }
  });

  const featureFlags: FeatureFlagsProp = {};

  // TODO: determine based on tenantId once we decide what to do with IAD
  featureFlags.enableIAD = false;

  const terminalErrorScreen = terminalError
    ? ((terminalError.code && errorScreens[terminalError.code]) ?? (
        <InformationDialog text={t('errors.something-unexpected')} />
      ))
    : undefined;

  const { Content } = Layout;
  return (
    <Layout className={'tenant-lookup-window'}>
      <Content>
        <Row className="tenant-lookup-container">
          <Col xs={0} sm={0} md={12} lg={14} className="portal-info">
            <div className="tenant-lookup-info-steps">
              <h1>{t(INFO_TITLE_KEYS[resource])}</h1>
              <Separator />
              <div>
                <p>{t('tenant-lookup.info.steps-1')}</p>
                <p>{t('tenant-lookup.info.steps-2')}</p>
              </div>
            </div>

            {/* Footer */}
            <div className={'tenant-lookup-info-footer'}>
              <p>
                © {new Date().getFullYear()} Imprivata, Inc. All rights
                reserved.
              </p>
            </div>
          </Col>
          <Col xs={24} sm={24} md={12} lg={10}>
            <div className={'login-container'}>
              <AuthnLayout
                hexImage={customHexagonImage}
                header={header}
                logoUrl={logoUrl}
                hideLayout={
                  actualView === AuthnViews.INFORMATION_DIALOG ||
                  terminalErrorScreen !== undefined
                }
              >
                {terminalErrorScreen !== undefined ? (
                  terminalErrorScreen
                ) : authDetails?.applicationId ? (
                  <>
                    {!authDetails.username && (
                      <UserNameForm
                        onSubmit={username => {
                          setAuthDetails({ ...authDetails, username });
                        }}
                        autoComplete={disableAutoComplete ? 'off' : undefined}
                      />
                    )}
                    {authDetails.username && (
                      <Authn
                        tenantId={authDetails?.tenantId}
                        onEvent={handleEvent}
                        deviceFactors={deviceFactors}
                        requestConfig={requestConfig}
                        tracing={tracing}
                        applicationId={authDetails?.applicationId}
                        allowChangeUsername={!!authDetails?.applicationId}
                        featureFlags={featureFlags}
                        autoComplete={disableAutoComplete ? 'off' : undefined}
                      />
                    )}
                  </>
                ) : (
                  <LoginComponent
                    onSubmit={handleSubmit}
                    autoComplete={disableAutoComplete ? 'off' : undefined}
                  />
                )}
              </AuthnLayout>
            </div>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export { TenantLogin };
